import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { addMonths, formatDate, makeCalendarDays, makeCalendarRange } from "../../utils/calendarUtils";
import { isSameDate, overlapsRange } from "../../utils/timelineUtils";

const laneLimit = 4;

const getCalendarWeeks = (days = []) =>
  Array.from({ length: Math.ceil(days.length / 7) }, (_, index) => days.slice(index * 7, index * 7 + 7));

const getWeekSegment = (row, weekDays) => {
  const weekStart = weekDays[0];
  const weekEnd = weekDays[weekDays.length - 1];
  if (!overlapsRange(row, weekStart, weekEnd)) return null;

  const segmentStart = row.startDate > weekStart ? row.startDate : weekStart;
  const segmentEnd = row.dueDate < weekEnd ? row.dueDate : weekEnd;
  const startIndex = weekDays.findIndex((day) => isSameDate(day, segmentStart));
  const endIndex = weekDays.findIndex((day) => isSameDate(day, segmentEnd));

  return {
    startColumn: (startIndex >= 0 ? startIndex : 0) + 1,
    endColumn: (endIndex >= 0 ? endIndex : weekDays.length - 1) + 2,
    startsInWeek: isSameDate(segmentStart, row.startDate),
    endsInWeek: isSameDate(segmentEnd, row.dueDate),
  };
};

const getSegmentLabel = (segment, row) => {
  if (segment.startsInWeek) return `착수 · ${row.orderNo}`;
  if (segment.endsInWeek) return `납기 · ${row.orderNo}`;
  return row.orderNo;
};

const CalendarTooltipContent = ({ row }) => (
  <Box sx={{ minWidth: 250, maxWidth: 320 }}>
    <Typography sx={{ mb: 1, fontSize: 13.5, fontWeight: 900, lineHeight: 1.35, color: "#fff" }}>
      {row.orderNo || "-"} · {row.itemName || "품목명 없음"}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "56px minmax(0, 1fr)",
        gap: "6px 10px",
        fontSize: 12.5,
        lineHeight: 1.35,
      }}
    >
      {[
        ["거래처", row.customerName],
        ["품목", row.itemName],
        ["착수", row.startLabel],
        ["납기", row.dueLabel],
        ["여유일", row.availableMarginDays === null || row.availableMarginDays === undefined ? "-" : `${row.availableMarginDays}일`],
        ["일정", row.scheduleLabel],
        ["위험", row.riskLabel],
      ].map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Box component="span" sx={{ color: "#cbd5e1", fontWeight: 600 }}>
            {label}
          </Box>
          <Box component="strong" sx={{ minWidth: 0, color: "#fff", fontWeight: 800, wordBreak: "keep-all" }}>
            {value || "-"}
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const calendarTooltipProps = {
  arrow: true,
  placement: "top",
  enterDelay: 120,
  componentsProps: {
    tooltip: {
      sx: {
        p: 1.5,
        borderRadius: "8px",
        bgcolor: "#111827",
        boxShadow: "0 12px 28px rgba(15, 23, 42, 0.22)",
      },
    },
    arrow: {
      sx: {
        color: "#111827",
      },
    },
  },
};

export const OssDashboardConnectedCalendar = ({ rows = [], onOpenOrder }) => {
  const [month, setMonth] = useState(() => new Date());
  const autoFocusedRef = useRef(false);
  const range = useMemo(() => makeCalendarRange(month), [month]);
  const days = useMemo(() => makeCalendarDays(range.start, range.end), [range.start, range.end]);
  const weeks = useMemo(() => getCalendarWeeks(days), [days]);
  const visibleRows = useMemo(
    () => rows.filter((row) => overlapsRange(row, range.start, range.end)).slice(0, laneLimit),
    [rows, range.start, range.end]
  );

  useEffect(() => {
    if (autoFocusedRef.current || !rows[0]?.startDate) return;
    setMonth(rows[0].startDate);
    autoFocusedRef.current = true;
  }, [rows]);

  return (
    <Stack spacing={1.2} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={() => setMonth((prev) => addMonths(prev, -1))}
          sx={{ width: 34, height: 34, color: "#64748b" }}
        >
            <ChevronLeftRoundedIcon />
        </IconButton>
        <Typography sx={{ minWidth: 138, textAlign: "center", fontSize: 18, fontWeight: 900, color: "#111827", lineHeight: 1.2 }}>
          {month.getFullYear()}년 {month.getMonth() + 1}월
        </Typography>
        <IconButton
          size="small"
          onClick={() => setMonth((prev) => addMonths(prev, 1))}
          sx={{ width: 34, height: 34, color: "#64748b" }}
        >
            <ChevronRightRoundedIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", borderBottom: "1px solid #e5e7eb" }}>
          {["일", "월", "화", "수", "목", "금", "토"].map((day) => (
            <Box key={day} sx={{ p: 0.8, textAlign: "center", bgcolor: "#f8fafc", fontSize: 12.5, fontWeight: 800, color: "#475569" }}>
              {day}
            </Box>
          ))}
        </Box>
        <Box sx={{ position: "relative", height: "calc(100% - 35px)", overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
              height: "100%",
            }}
          >
            {days.map((day) => {
              const dayKey = formatDate(day);
              const inMonth = day.getMonth() === month.getMonth();
              return (
                <Box
                  key={dayKey}
                  sx={{
                    minHeight: 0,
                    p: 0.6,
                    borderRight: "1px solid #e5e7eb",
                    borderBottom: "1px solid #e5e7eb",
                    bgcolor: inMonth ? "#fff" : "#f8fafc",
                    opacity: inMonth ? 1 : 0.55,
                    overflow: "hidden",
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#111827" }}>
                    {day.getDate()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gridTemplateRows: `repeat(${weeks.length}, minmax(0, 1fr))`,
              pointerEvents: "none",
            }}
          >
            {weeks.flatMap((weekDays, weekIndex) =>
              visibleRows.map((row, rowIndex) => {
                const segment = getWeekSegment(row, weekDays);
                if (!segment) return null;
                const laneTop = 27 + rowIndex * 19;
                return (
                  <Tooltip
                    key={`${row.id}-${weekIndex}`}
                    title={<CalendarTooltipContent row={row} />}
                    {...calendarTooltipProps}
                  >
                    <Button
                      onClick={() => onOpenOrder?.(row.orderId)}
                      sx={{
                        gridColumn: `${segment.startColumn} / ${segment.endColumn}`,
                        gridRow: `${weekIndex + 1}`,
                        alignSelf: "start",
                        justifySelf: "stretch",
                        mt: `${laneTop}px`,
                        ml: segment.startsInWeek ? "6px" : 0,
                        mr: segment.endsInWeek ? "6px" : 0,
                        minWidth: 0,
                        minHeight: 18,
                        height: 18,
                        px: 0.6,
                        py: 0,
                        borderRadius: `${segment.startsInWeek ? 999 : 0}px ${segment.endsInWeek ? 999 : 0}px ${segment.endsInWeek ? 999 : 0}px ${segment.startsInWeek ? 999 : 0}px`,
                        bgcolor: row.riskBg,
                        color: row.riskTone,
                        border: `1px solid ${row.riskTone}30`,
                        boxShadow: `inset 0 0 0 1px ${row.riskTone}10`,
                        fontSize: "10.8px !important",
                        fontWeight: "800 !important",
                        lineHeight: 1,
                        overflow: "hidden",
                        textTransform: "none",
                        pointerEvents: "auto",
                        "&:hover": {
                          bgcolor: row.riskBg,
                          borderColor: `${row.riskTone}66`,
                          boxShadow: `0 2px 8px ${row.riskTone}22`,
                        },
                      }}
                    >
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {getSegmentLabel(segment, row)}
                      </Box>
                    </Button>
                  </Tooltip>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
